import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { supabase } from '@readsavor/supabase-client';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';
import { useAuthStore } from '../store/auth.store';
import { useQueryClient } from '@tanstack/react-query'; // 引入 hook
const RECOVERY_LOCK_KEY = 'auth_recovery_in_progress';
/**
 * AuthProvider (Final, Robust Version)
 *
 * This component synchronizes Supabase auth events with the global `useAuthStore`.
 * It uses a `localStorage` key (`RECOVERY_LOCK_KEY`) as a global "lock" to
 * correctly handle the password recovery flow across all tabs, preventing
 * race conditions where a non-recovery tab might auto-login.
 */
export const AuthProvider = ({ children }) => {
    const { session, setSession, setStatus, setInitialized, logout, setRealtimeReady, setInitializationComplete, } = useAuthStore();
    const queryClient = useQueryClient();
    // Effect to manage and centralize the realtime connection status via a "sentinel" channel.
    // This effect is dependent on the user's session.
    useEffect(() => {
        // We only attempt to establish a realtime connection if the user is logged in.
        if (!session) {
            // If there's no session, ensure the realtime status is false.
            setRealtimeReady(false);
            return;
        }
        // This channel does not listen to any data. Its sole purpose is to reflect
        // the state of the underlying websocket connection.
        const sentinelChannel = supabase.channel('auth-provider-sentinel');
        sentinelChannel.subscribe((status, err) => {
            if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
                // When the sentinel channel is successfully subscribed, we know the websocket
                // is connected and authenticated. This is our signal that realtime is ready.
                setRealtimeReady(true);
            }
            else if (status === REALTIME_SUBSCRIBE_STATES.CLOSED ||
                status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
                // If the sentinel channel closes or errors, the underlying connection is gone.
                console.warn('[AuthProvider] Realtime connection is closed (via sentinel channel).');
                setRealtimeReady(false);
            }
            if (err) {
                // This can happen on initial connection setup, especially with network fluctuations.
                // Supabase client will handle the retry automatically. We log it as a warning
                // instead of an error because it's transient and recoverable.
                console.warn('[AuthProvider] Sentinel channel encountered a transient error:', err.message);
            }
        });
        // Cleanup the sentinel channel on component unmount or when the session is lost.
        return () => {
            supabase.removeChannel(sentinelChannel);
            setRealtimeReady(false); // Ensure status is reset on cleanup
        };
    }, [session, setRealtimeReady]);
    useEffect(() => {
        setStatus('pending');
        setInitialized(false);
        // Get the initial session, but DO NOT set the state here.
        // This is only to potentially speed up the first render for a truly existing session.
        // The onAuthStateChange listener is the SINGLE SOURCE OF TRUTH.
        supabase.auth.getSession().then(({ data }) => {
            if (!useAuthStore.getState().isInitialized) {
                // If after checking, no event has arrived yet, it's safe to assume
                // we are unauthenticated for now. The listener will correct it if needed.
                const isRecoveryInProgress = localStorage.getItem(RECOVERY_LOCK_KEY) === 'true';
                if (!data.session && !isRecoveryInProgress) {
                    setStatus('unauthenticated');
                }
                setInitialized(true);
            }
        });
        // Core Logic: Listen for all auth events. This is the single source of truth.
        const { data: { subscription }, } = supabase.auth.onAuthStateChange(async (event, session) => {
            const isRecoveryInProgress = localStorage.getItem(RECOVERY_LOCK_KEY) === 'true';
            // --- Global Recovery Lock ---
            // This is the most critical part of the logic.
            // If the lock is active, we must ensure the state is STABLE at 'password_recovery'.
            if (isRecoveryInProgress) {
                // If the state isn't already 'password_recovery', force it to be.
                // This prevents race conditions where a subsequent SIGNED_IN event might
                // briefly change the state before this check runs.
                if (useAuthStore.getState().status !== 'password_recovery') {
                    setStatus('password_recovery');
                }
                // If the global lock is active, we ONLY allow two events to proceed:
                // 1. PASSWORD_RECOVERY: The event that starts the flow.
                // 2. SIGNED_OUT: The event that ends the flow.
                if (event !== 'PASSWORD_RECOVERY' && event !== 'SIGNED_OUT') {
                    console.warn(`[AuthProvider] Ignoring event "${event}" because a password recovery is in progress.`);
                    // By returning here, we prevent other tabs from incorrectly interpreting
                    // the recovery session as a normal login.
                    return;
                }
            }
            switch (event) {
                case 'PASSWORD_RECOVERY':
                    // Set the global lock to prevent other tabs from auto-logging in.
                    localStorage.setItem(RECOVERY_LOCK_KEY, 'true');
                    setSession(session);
                    setStatus('password_recovery');
                    break;
                case 'SIGNED_IN':
                    // This block will now only be reached if the recovery lock is NOT active.
                    if (session) {
                        setSession(session);
                        setStatus('authenticated');
                        // Force refetch user-specific data to ensure all tabs are synchronized.
                        queryClient.refetchQueries({ queryKey: ['user'] });
                    }
                    break;
                case 'TOKEN_REFRESHED':
                case 'USER_UPDATED':
                    setSession(session);
                    setStatus('authenticated');
                    break;
                case 'SIGNED_OUT':
                    // This is the only way to exit the recovery flow.
                    // Always clear the lock on sign out.
                    localStorage.removeItem(RECOVERY_LOCK_KEY);
                    setSession(null);
                    setStatus('unauthenticated');
                    setInitializationComplete(false);
                    break;
                case 'INITIAL_SESSION':
                    // This is for the initial load. If a recovery was interrupted,
                    // this logic might run. The lock check at the top prevents issues.
                    if (session) {
                        setSession(session);
                        setStatus('authenticated');
                    }
                    else {
                        // If there's no session, ensure any lingering lock is cleared.
                        localStorage.removeItem(RECOVERY_LOCK_KEY);
                        setStatus('unauthenticated');
                        setInitializationComplete(false);
                    }
                    break;
            }
            // Ensure the app is marked as initialized after the first event.
            if (!useAuthStore.getState().isInitialized) {
                setInitialized(true);
            }
        });
        // Cleanup the listener when the component unmounts.
        return () => {
            subscription.unsubscribe();
        };
    }, [setSession, setStatus, setInitialized, setInitializationComplete, queryClient]);
    // Global 401 error handling remains the same.
    useEffect(() => {
        const handleUnauthorized = (event) => {
            const { error, source } = event.detail || {};
            // Only trigger logout if we are currently authenticated.
            if (useAuthStore.getState().status === 'authenticated') {
                console.warn(`[AuthProvider] Unauthorized error from ${source}, triggering logout:`, error);
                logout(queryClient);
            }
        };
        window.addEventListener('auth:unauthorized', handleUnauthorized);
        return () => {
            window.removeEventListener('auth:unauthorized', handleUnauthorized);
        };
    }, [logout, queryClient]);
    return _jsx(_Fragment, { children: children });
};
//# sourceMappingURL=AuthProvider.js.map